import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInView, useReducedMotion } from "framer-motion";
import { MdAutoAwesome } from "react-icons/md";
import { FaArrowRight } from "react-icons/fa";

/**
 * The bio, presented the way the AI features in the projects work: pick a question and the
 * answer is "generated" in front of you, then backed by sources — the case studies that prove it.
 */
const TOPICS = [
  {
    id: "who",
    question: "Who are you?",
    answer:
      "I'm Wafa Ben Miloud, a software engineer who builds complete products end to end — database, API and interface. AI is one of the tools I bring to that, not the whole story.",
    sources: [
      { label: "BPMFlow", to: "/projects/bpmflow" },
      { label: "ImmoFlow", to: "/projects/immoflow" },
      { label: "CRMBuzz", to: "/projects/crmbuzz" },
    ],
  },
  {
    id: "build",
    question: "What do you build?",
    answer:
      "Whole systems rather than screens. I design the flow myself — schema, API contracts, authentication and the UI on top — from a multi-agency real estate platform to a workflow engine split into microservices and a white-label CRM with its own website builder.",
    sources: [
      { label: "ImmoFlow", to: "/projects/immoflow" },
      { label: "BPMFlow", to: "/projects/bpmflow" },
      { label: "CRMBuzz", to: "/projects/crmbuzz" },
    ],
  },
  {
    id: "ai",
    question: "How do you use AI?",
    answer:
      "As a feature inside a real product, on a short leash. I work with LLMs, RAG and semantic search, and I make the model earn trust: answers grounded in real data and shown with their sources, outputs validated before anything is saved.",
    sources: [
      { label: "Aplivia", to: "/projects/aplivia" },
      { label: "CRMBuzz", to: "/projects/crmbuzz" },
      { label: "CodeQuery", to: "/projects/codequery" },
    ],
  },
  {
    id: "stack",
    question: "What's your stack?",
    answer:
      "React or Angular on the frontend, Java Spring Boot or Node.js behind it, PostgreSQL or MongoDB underneath — and Python when the work is NLP. I pick up an unfamiliar stack quickly when a project calls for it.",
    sources: [
      { label: "BPMFlow", to: "/projects/bpmflow" },
      { label: "ImmoFlow", to: "/projects/immoflow" },
      { label: "CodeQuery", to: "/projects/codequery" },
    ],
  },
  {
    id: "work",
    question: "How do you work?",
    answer:
      "I give the unglamorous parts the same attention as the feature — deployment, monitoring, data integrity — because that is where systems usually break. And I'd rather ship something people use than a demo.",
    sources: [
      { label: "BPMFlow", to: "/projects/bpmflow" },
      { label: "Aplivia", to: "/projects/aplivia" },
    ],
  },
];

const CHARS_PER_TICK = 2;
const TICK_MS = 18;

const AboutAsk = () => {
  const [active, setActive] = useState(0);
  const [typed, setTyped] = useState(0);
  const panelRef = useRef(null);
  const inView = useInView(panelRef, { once: true, amount: 0.3 });
  const reducedMotion = useReducedMotion();

  const topic = TOPICS[active];
  const done = typed >= topic.answer.length;

  // Type the answer out once the panel is on screen; restart whenever the question changes.
  useEffect(() => {
    if (!inView) return undefined;
    if (reducedMotion) {
      setTyped(topic.answer.length);
      return undefined;
    }
    setTyped(0);
    const timer = setInterval(() => {
      setTyped((n) => {
        if (n >= topic.answer.length) {
          clearInterval(timer);
          return n;
        }
        return n + CHARS_PER_TICK;
      });
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [active, inView, reducedMotion, topic.answer.length]);

  return (
    <div className="about-ask" ref={panelRef}>
      <div className="about-ask__bar">
        <span className="about-ask__dots" aria-hidden="true"><i /><i /><i /></span>
        <span className="about-ask__title">ask-wafa</span>
        <span className="about-ask__badge">
          <MdAutoAwesome aria-hidden="true" /> Grounded in my projects
        </span>
      </div>

      <div className="about-ask__questions" role="group" aria-label="Questions about me">
        {TOPICS.map((t, i) => (
          <button
            key={t.id}
            type="button"
            className={`about-ask__chip${i === active ? " is-active" : ""}`}
            aria-pressed={i === active}
            onClick={() => setActive(i)}
          >
            {t.question}
          </button>
        ))}
      </div>

      <div className="about-ask__thread">
        <div className="about-ask__question">{topic.question}</div>

        {/* Clicking the answer skips the typing. The invisible copy reserves the final height. */}
        <div className="about-ask__answer" onClick={() => setTyped(topic.answer.length)}>
          <span className="about-ask__avatar" aria-hidden="true"><MdAutoAwesome /></span>
          <div className="about-ask__text">
            <span className="about-ask__ghost" aria-hidden="true">{topic.answer}</span>
            <span className="about-ask__typed" aria-hidden="true">
              {topic.answer.slice(0, typed)}
              {!done && <span className="about-ask__caret" />}
            </span>
            <span className="about-ask__sr">{topic.answer}</span>
          </div>
        </div>

        <div className={`about-ask__sources${done ? " is-visible" : ""}`}>
          <span className="about-ask__sources-label">Sources</span>
          {topic.sources.map((s, i) => (
            <Link key={s.to} to={s.to} className="about-ask__source" tabIndex={done ? 0 : -1}>
              <span className="about-ask__source-index">[{i + 1}]</span> {s.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="about-ask__footer">
        <span className="about-ask__hint">Pick a question — every answer cites the work behind it.</span>
        <a href="#contact" className="btn about-ask__cta">
          Let's Talk! <FaArrowRight size={11} />
        </a>
      </div>
    </div>
  );
};

export default AboutAsk;
